// Always-on account autosave for plan settings.
//
// Named work writes directly to public.client_plans. Unnamed work writes to the
// user's single public.plan_autosaves row. Both paths reuse the census-free
// clientPlanPayload allowlist; employee rows and calculated employee results
// never enter this module or either database table.
import { getCachedAuthSession } from '@/lib/authApi'
import { supabaseEnv } from '@/lib/supabaseClient'
import { getPlanAutosave, updateClientPlan, upsertPlanAutosave } from '@/lib/clientsApi'

const DEBOUNCE_MS = 2000
const LOCAL_PLAN_UPDATED_KEY = 'retira-plan-settings-local-updated-at'
const ONBOARDING_DONE_KEY = 'retira-onboarding-done'

let retryCurrentSave = null
let flushCurrentSave = null

function planSettingsJson(useStore) {
  try {
    return JSON.stringify(useStore.getState().getPlanSettings())
  } catch {
    return null
  }
}

function currentOwnerId() {
  return getCachedAuthSession()?.user?.id || null
}

function markLocalPlanChanged() {
  const timestamp = new Date().toISOString()
  try {
    window.localStorage.setItem(LOCAL_PLAN_UPDATED_KEY, timestamp)
  } catch {
    // Browser storage can be unavailable in hardened/private contexts. Cloud
    // autosave still works; recovery simply becomes conservative.
  }
  return timestamp
}

function readLocalPlanTimestamp() {
  try {
    return window.localStorage.getItem(LOCAL_PLAN_UPDATED_KEY)
  } catch {
    return null
  }
}

function setStatus(useClientBook, status, options = {}) {
  useClientBook?.getState?.().setSyncStatus?.(status, options)
}

export function wirePlanAutosave(useStore, useClientBook) {
  if (typeof window === 'undefined' || !useStore?.subscribe) return () => {}

  let lastSavedJson = planSettingsJson(useStore)
  let pendingJson = null
  let timer = null
  let inFlight = null

  const save = async (json) => {
    const ownerId = currentOwnerId()
    if (!ownerId || !supabaseEnv.isReady) return false
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      pendingJson = json
      setStatus(useClientBook, 'offline')
      return false
    }

    setStatus(useClientBook, 'saving', { error: null })
    try {
      const clientBook = useClientBook?.getState?.()
      if (clientBook?.enabled && clientBook.activeClientId) {
        const row = await updateClientPlan(clientBook.activeClientId, {
          planSettings: useStore.getState().getPlanSettings(),
        })
        clientBook.markClientAutosaved?.(row)
      } else {
        await upsertPlanAutosave({
          ownerId,
          planSettings: useStore.getState().getPlanSettings(),
        })
        setStatus(useClientBook, 'saved', { lastSyncedAt: new Date().toISOString(), error: null })
      }
      lastSavedJson = json
      return true
    } catch (error) {
      pendingJson = json
      setStatus(useClientBook, 'error', { error })
      return false
    }
  }

  const flush = async () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (inFlight) await inFlight
    if (!pendingJson || pendingJson === lastSavedJson) return true
    const json = pendingJson
    pendingJson = null
    inFlight = save(json)
    const result = await inFlight
    inFlight = null
    return result
  }

  retryCurrentSave = () => {
    pendingJson = planSettingsJson(useStore)
    return flush()
  }
  flushCurrentSave = flush

  const unsubscribe = useStore.subscribe(() => {
    const json = planSettingsJson(useStore)
    if (!json || json === lastSavedJson || json === pendingJson) return
    markLocalPlanChanged()
    pendingJson = json
    setStatus(useClientBook, navigator.onLine === false ? 'offline' : 'saving', { error: null })
    if (timer) clearTimeout(timer)
    timer = setTimeout(flush, DEBOUNCE_MS)
  })

  const flushOnHide = () => {
    if (document.visibilityState === 'hidden') void flush()
  }
  const handleOffline = () => setStatus(useClientBook, 'offline')
  const handleOnline = () => {
    pendingJson = planSettingsJson(useStore)
    void flush()
  }
  document.addEventListener('visibilitychange', flushOnHide)
  window.addEventListener('pagehide', flush)
  window.addEventListener('offline', handleOffline)
  window.addEventListener('online', handleOnline)

  return () => {
    unsubscribe()
    document.removeEventListener('visibilitychange', flushOnHide)
    window.removeEventListener('pagehide', flush)
    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleOnline)
    if (timer) clearTimeout(timer)
    if (retryCurrentSave) retryCurrentSave = null
    if (flushCurrentSave === flush) flushCurrentSave = null
  }
}

export function retryPlanAutosave() {
  return retryCurrentSave?.() || Promise.resolve(false)
}

export function flushPlanAutosave() {
  return flushCurrentSave?.() || Promise.resolve(true)
}

// Recover an unnamed draft on a new device, but never overwrite a known newer
// browser copy. Existing browsers from before timestamp tracking are treated
// conservatively once onboarding has been completed.
export async function restorePlanAutosave(useStore, useClientBook) {
  if (typeof window === 'undefined' || !currentOwnerId() || !supabaseEnv.isReady) return false
  if (useClientBook?.getState?.().activeClientId) return false

  try {
    const draft = await getPlanAutosave()
    if (!draft?.plan_settings) {
      setStatus(useClientBook, navigator.onLine === false ? 'offline' : 'saved')
      return false
    }

    const localUpdatedAt = readLocalPlanTimestamp()
    const completedLocally = window.localStorage.getItem(ONBOARDING_DONE_KEY) === 'true'
    const cloudTime = Date.parse(draft.updated_at || '')
    const localTime = Date.parse(localUpdatedAt || '')
    const shouldRestore = localUpdatedAt
      ? Number.isFinite(cloudTime) && (!Number.isFinite(localTime) || cloudTime > localTime)
      : !completedLocally

    if (shouldRestore) {
      useStore.getState().importPlanSettings(draft.plan_settings)
      useStore.temporal?.getState?.().clear?.()
      try {
        window.localStorage.setItem(LOCAL_PLAN_UPDATED_KEY, draft.updated_at || new Date().toISOString())
      } catch {
        // See markLocalPlanChanged.
      }
    }
    setStatus(useClientBook, navigator.onLine === false ? 'offline' : 'saved', {
      lastSyncedAt: draft.updated_at || new Date().toISOString(),
      error: null,
    })
    return shouldRestore
  } catch (error) {
    setStatus(useClientBook, navigator.onLine === false ? 'offline' : 'error', { error })
    return false
  }
}
